import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Button, Input, Modal, Typography, Form } from 'antd';
import { LoadingState, useLoading } from '@monorepo/react-components';

import { Video } from '@monorepo/types';
import { useNotification } from '../../../../hooks/use-notification';
import { useStore } from '../../../../helpers/use-store';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

export interface EditVideoTitleModalActions {
  open: (video: Video) => void;
}

interface Props {
  onClose: () => Promise<void>;
}

const EditVideoTitleModal = forwardRef<EditVideoTitleModalActions, Props>(
  ({ onClose }, ref) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [currentVideo, setCurrentVideo] = useState<Video | null>(null);
    const [newTitle, setNewTitle] = useState('');
    const { loadingState, updateLoadingState } = useLoading();
    const notificationApi = useNotification();
    const {
      dataStore: { videoStore },
    } = useStore();

    useImperativeHandle(ref, () => ({
      open: (video: Video) => {
        setCurrentVideo(video);
        setNewTitle(video.title);
        setIsOpen(true);
      },
    }));

    const handleCancel = () => {
      setIsOpen(false);
      setCurrentVideo(null);
      setNewTitle('');
    };

    const handleSave = async () => {
      if (!currentVideo) return;

      try {
        updateLoadingState(LoadingState.Loading);

        await videoStore.updateMetadata(currentVideo._id.toString(), {
          title: newTitle,
        });

        notificationApi.success({
          placement: 'top',
          message: t('common.success'),
          description: t('series.videos.editTitleModal.success'),
        });

        handleCancel();
      } catch (error) {
        console.error('Failed to update video title:', error);
        notificationApi.error({
          placement: 'top',
          message: t('series.videos.editTitleModal.error'),
          description: t('series.videos.editTitleModal.errorDesc'),
        });
      } finally {
        updateLoadingState(LoadingState.Loaded);
        await onClose();
      }
    };

    if (!currentVideo) {
      return null;
    }

    return (
      <Modal
        title={
          <Typography.Title style={{ margin: 0 }} level={5}>
            {t('series.videos.editTitleModal.title')}
          </Typography.Title>
        }
        open={isOpen}
        onCancel={(e) => {
          e.stopPropagation();
          handleCancel();
        }}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            {t('series.videos.editTitleModal.cancel')}
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            loading={loadingState === LoadingState.Loading}
            disabled={!newTitle.trim() || newTitle === currentVideo.title}
          >
            {t('series.videos.editTitleModal.save')}
          </Button>,
        ]}
      >
        <Form layout="vertical">
          <Form.Item>
            <Input
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder={t('series.videos.editTitleModal.placeholder')}
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  }
);

export default EditVideoTitleModal;
